import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Grid, LinearProgress, Hidden, useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { FiberManualRecord as FiberManualRecordIcon } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import {
  GridToolbarContainer, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarFilterButton,
} from '@material-ui/data-grid';
import moment from 'moment';
import i18n from '@i18n';
import { useAnalisis } from '@hooks';
import { Alert, ChipStep, DataGrid } from '@components/atoms';
import constants from '@helpers/constants';
import AuthContext from '@contexts/AuthContext';

const useStyles = makeStyles({
  stepLink: {
    width: '100%',
    cursor: 'default',
    '&:hover': {
      cursor: 'pointer',
      '& .MuiChip-root': {
        cursor: 'pointer',
        textDecoration: 'underline',
        opacity: 0.9,
      },
    },
  },
  dataGrid: {
    '& .MuiDataGrid-columnHeaders': {
      minHeight: '56px !important',
      maxHeight: 'none !important',
      height: 'auto !important',
    },
    '& .MuiDataGrid-columnHeader': {
      height: 'auto !important',
      maxHeight: 'none !important',
      paddingTop: 8,
      paddingBottom: 8,
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      whiteSpace: 'normal',
      justifyContent: 'center',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      whiteSpace: 'pre-line',
      lineHeight: 1.2,
      textAlign: 'center',
      overflow: 'visible',
    },
  },
});

const parseLimitDate = (limitDate) => {
  if (!limitDate) return null;
  const date = moment(limitDate.toString().replace('Z', ''));
  return date.isValid() ? date : null;
};

const formatCreatedAt = (createdAt) => {
  if (!createdAt) return '';
  const date = moment(createdAt.toString().replace('Z', ''));
  return date.isValid() ? date.format('DD-MM-YYYY') : '';
};

const getTrafficLightColor = (limitDate) => {
  const date = parseLimitDate(limitDate);
  if (!date) return null;
  const days = date.clone().startOf('day').diff(moment().startOf('day'), 'days');
  if (days <= 2) return '#d32f2f';
  if (days <= 5) return '#fbc02d';
  return '#388e3c';
};

const getDaysToLimit = (limitDate) => {
  const date = parseLimitDate(limitDate);
  if (!date) return null;
  return date.clone().startOf('day').diff(moment().startOf('day'), 'days');
};

const formatMembers = (members) => {
  if (!members || members.length === 0) return '';
  return members.map((member) => `${member.name} ${member.lastname}`).join(', ');
};

const compareDates = (date1, date2, descending = false) => {
  if (!date1 && !date2) return 0;
  if (!date1) return 1;
  if (!date2) return -1;
  const diff = date1.valueOf() - date2.valueOf();
  return descending ? -diff : diff;
};

const LimitDateCell = ({ value, row }) => {
  const date = parseLimitDate(value);
  if (!date) return null;
  const isFinished = row && (row.step === 'completado' || row.archived);
  const color = isFinished ? null : getTrafficLightColor(value);
  return (
    <Grid container alignItems="center" justifyContent="center" spacing={1} wrap="nowrap">
      {color && (
        <Grid item>
          <FiberManualRecordIcon style={{ color, fontSize: 16 }} />
        </Grid>
      )}
      <Grid item>
        {date.format('DD-MM-YYYY')}
      </Grid>
    </Grid>
  );
};

LimitDateCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  row: PropTypes.shape({
    step: PropTypes.string,
    archived: PropTypes.bool,
  }),
};

LimitDateCell.defaultProps = {
  value: null,
  row: null,
};

const StepCell = ({ row, id, disabled }) => {
  const history = useHistory();
  const classes = useStyles();
  const step = constants.STEPS_ANALISIS.find((item) => item.step === row.step);

  if (!step) return null;

  if (disabled) {
    return <ChipStep {...step} />;
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={classes.stepLink}
      onClick={() => history.push(`/e/analisis/edit/${id}`)}
      onKeyPress={(event) => {
        if (event.key === 'Enter') {
          history.push(`/e/analisis/edit/${id}`);
        }
      }}
    >
      <ChipStep {...step} />
    </div>
  );
};

StepCell.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  disabled: PropTypes.bool,
  row: PropTypes.shape({
    step: PropTypes.string,
  }).isRequired,
};

StepCell.defaultProps = {
  disabled: false,
};

const List = () => {
  const classes = useStyles();
  const { pages: { employees: { analisis: { list: texts } } } } = i18n;
  const { getAnalisisAsignados, analisisError, isAnalisisLoading } = useAnalisis();
  const [analisis, setAnalisis] = useState(null);
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const stepAsignar = user && user.rolAnalisis
    ? !user.rolAnalisis.steps.filter((step) => step.id === 'asignacion')[0].enabled
    : false;
  const ltSmSize = useMediaQuery(theme.breakpoints.down('sm'));
  const onlyxsSize = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(async () => {
    const data = await getAnalisisAsignados();
    const list = Array.isArray(data) ? data : [];
    setAnalisis(list.filter((item) => !item.archived));
  }, []);

  const limitDateColumn = {
    field: 'limitDate',
    headerAlign: 'center',
    headerName: texts.columns.limitDate,
    align: 'center',
    type: 'date',
    sortComparator: (v1, v2, param1, param2) => {
      const row1 = param1 && param1.api && param1.api.getRow ? param1.api.getRow(param1.id) : null;
      const row2 = param2 && param2.api && param2.api.getRow ? param2.api.getRow(param2.id) : null;
      const completed1 = row1 && row1.step === 'completado';
      const completed2 = row2 && row2.step === 'completado';
      if (completed1 !== completed2) {
        return completed1 ? 1 : -1;
      }
      const date1 = parseLimitDate(v1);
      const date2 = parseLimitDate(v2);
      // Completados: más reciente primero; resto: más próximo a vencer primero
      return compareDates(date1, date2, completed1 && completed2);
    },
    renderCell: LimitDateCell,
  };

  const daysToLimitColumn = {
    field: 'daysToLimit',
    headerAlign: 'center',
    headerName: texts.columns.daysToLimit,
    align: 'center',
    sortable: false,
    valueGetter: (params) => {
      const { row } = params;
      if (row.step === 'completado' || row.archived) {
        return null;
      }
      return getDaysToLimit(row.limitDate);
    },
    valueFormatter: (params) => {
      const { value } = params;
      return value === null || value === undefined ? '' : value;
    },
  };

  const membersColumn = {
    field: 'members',
    headerName: texts.columns.members,
    headerAlign: 'center',
    align: 'center',
    valueGetter: (params) => formatMembers(params.value),
  };

  const stepColumn = {
    field: 'step',
    headerName: texts.columns.step,
    headerAlign: 'center',
    align: 'center',
    renderCell: (params) => {
      const { id, row } = params;
      return (
        <StepCell
          id={id}
          row={row}
          disabled={stepAsignar}
        />
      );
    },
  };

  const columnsSm = [
    {
      field: 'number',
      headerName: texts.columns.number,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'customer',
      headerName: texts.columns.customer,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (data) => (data.value.name),
    },
    {
      field: 'sample',
      headerName: texts.columns.sample,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (data) => (data.value),
    },
    {
      field: 'createdAt',
      headerAlign: 'center',
      headerName: texts.columns.createdAt,
      align: 'center',
      type: 'date',
      valueFormatter: (values) => formatCreatedAt(values.value),
    },
    {
      ...limitDateColumn,
    },
    {
      ...daysToLimitColumn,
    },
    {
      ...membersColumn,
    },
    {
      ...stepColumn,
    },
  ];

  const columns = [
    {
      field: 'number',
      headerName: texts.columns.number,
      flex: 0.7,
      minWidth: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'customer',
      headerName: texts.columns.customer,
      flex: 1.1,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (data) => (data.value.name),
    },
    {
      field: 'sample',
      headerName: texts.columns.sample,
      flex: 1.8,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (data) => (data.value),
    },
    {
      field: 'createdAt',
      headerAlign: 'center',
      headerName: texts.columns.createdAt,
      align: 'center',
      flex: 0.55,
      minWidth: 70,
      maxWidth: 100,
      type: 'date',
      valueFormatter: (values) => formatCreatedAt(values.value),
    },
    {
      ...limitDateColumn,
      flex: 0.85,
      minWidth: 85,
    },
    {
      ...daysToLimitColumn,
      flex: 0.55,
      minWidth: 70,
      maxWidth: 100,
    },
    {
      ...membersColumn,
      flex: 1.3,
      minWidth: 120,
    },
    {
      ...stepColumn,
      flex: 1,
      minWidth: 110,
    },
  ];

  const CustomToolbar = () => (
    <>
      <Hidden xsDown>
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector />
          <GridToolbarFilterButton />
        </GridToolbarContainer>
      </Hidden>
      <Hidden smUp>
        <GridToolbarContainer>
          <GridToolbarFilterButton />
          <GridToolbarColumnsButton />
        </GridToolbarContainer>
      </Hidden>
    </>
  );

  return (
    <>
      {analisisError && <Alert>{analisisError.message}</Alert>}
      {isAnalisisLoading && <LinearProgress />}
      {analisis && (
        <Grid container spacing={2}>
          <DataGrid
            className={classes.dataGrid}
            sortModel={[{ field: 'limitDate', sort: 'asc' }]}
            columns={onlyxsSize ? columnsSm : columns}
            rows={analisis}
            pageSize={ltSmSize ? 8 : 25}
            components={{
              Toolbar: CustomToolbar,
            }}
          />
        </Grid>
      )}
    </>
  );
};

List.propTypes = {
};

List.defaultProps = {
};

export default List;
